/* eslint-disable react-hooks/exhaustive-deps */
import React, {ChangeEvent, useContext, useEffect, useState} from 'react';
import {CATEGORY, FILTER_VALUES, FILTERING_RANGE, MULTI_FILTER_VALUES} from '../ISRConstants';
import {
    TFieldDataConfiguration,
    TFilterArrayItem, TIndexState,
    TUpsertISRProjectDataModelsInput
} from '../Pages/DefineModel/model';
import {getAssociatedAttributes, getCategoryTypeAndCategory} from '../Pages/DefineModel/ModelServices/ModalServices';
import {DeleteIcon} from "../../../../assests/GmsIcons";
import {toast} from "react-toastify";
import ConfirmDialog from "./ConfirmDialog";
import {AppContext} from "../../../../Context/AppContext";
import ToggleButton from "../../../../Components/Common/components/Inputs/ToggleButton";
import {ISRContext} from "../Context/ISRContext";

type TMultiChoiceProps = {
    id: string;
    fieldDataConfiguration: TFieldDataConfiguration;
    setFieldDataConfiguration: React.Dispatch<React.SetStateAction<TFieldDataConfiguration>>;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
    indexInfo: TIndexState;
    hasRange?: boolean;
    isRangeField?: boolean;
}

const ConfigurationFilterComponent = ({id, fieldDataConfiguration, setFieldDataConfiguration, userProjectDataModels, indexInfo, hasRange = false, isRangeField = false}: TMultiChoiceProps) => {
    const [attributesMap, setAttributesMap] = useState<Record<string, any[]>>({});
    const [showDialog, setShowDialog] = useState<any>(null);
    const [categoryData, setCategoryData] = useState([]);
    let appContext = useContext(AppContext);
    let { networkUserProjectDataModels } = useContext(ISRContext);
    let industryId = appContext.get_selected_industry() && appContext?.get_selected_industry()?.id;

    const createMultiFilter = async () => {
        const updatedFieldDataConfiguration: TFieldDataConfiguration = {...fieldDataConfiguration};
        if (!updatedFieldDataConfiguration.multiFilter) {
            updatedFieldDataConfiguration.multiFilter = {type: 'single', logic: 'allTrue'};
        }

        const dataModelFound = networkUserProjectDataModels.project.dataModels[indexInfo.dataModelIndex ?? -1];
        let subModelId: any = '';
        if (dataModelFound) {
            subModelId = dataModelFound.id;
        }
        if (!updatedFieldDataConfiguration.multiFilter?.filters) {
            updatedFieldDataConfiguration.multiFilter.filters = [];
        }
        const filters = fieldDataConfiguration?.multiFilter?.filters ?? [];
        let categoryId = '';
        if (fieldDataConfiguration.multiFilter?.type === 'single' && filters.length > 0) {
            if (!filters[0].categoryId) {
                toast.error('Please select category to continue');
                return;
            }
            categoryId = filters[0].categoryId;
        } else {
            if (dataModelFound) {
                subModelId = dataModelFound.id;
                let subModelCategories = dataModelFound.data.productCategories.map((e: any) => e.data.categories.data.map((category: any) => category.id)).flat(1);
                if (subModelCategories.length === 1) {
                    categoryId = subModelCategories[0];
                    await fetchAssociatedAttributes(categoryId);
                }
            }
        }
        updatedFieldDataConfiguration.multiFilter.filters.push({
            operator: '=',
            subModelId: subModelId ?? "",
            categoryId: categoryId,
            attributeId: ""
        });
        setFieldDataConfiguration(updatedFieldDataConfiguration);
    };

    const updateMultiFilterType = (type: string) => {
        const updatedFieldDataConfiguration: TFieldDataConfiguration = {...fieldDataConfiguration};
        if (!updatedFieldDataConfiguration.multiFilter) {
            updatedFieldDataConfiguration.multiFilter = {type: type, logic: 'allTrue'};
        } else {
            updatedFieldDataConfiguration.multiFilter.type = type;
            updatedFieldDataConfiguration.multiFilter.filters = [];
        }
        setFieldDataConfiguration(updatedFieldDataConfiguration);
    };

    const updateMultiFilterLogic = async (logic: string) => {
        const updatedFieldDataConfiguration: TFieldDataConfiguration = {...fieldDataConfiguration};
        if (updatedFieldDataConfiguration.multiFilter) {
            updatedFieldDataConfiguration.multiFilter.logic = logic;
            setFieldDataConfiguration(updatedFieldDataConfiguration);
        }
    };

    const handleCategoryDelete = async (index: number) => {
        const updatedFieldDataConfiguration: TFieldDataConfiguration = {...fieldDataConfiguration};
        updatedFieldDataConfiguration.multiFilter?.filters?.splice(index, 1);
        setFieldDataConfiguration(updatedFieldDataConfiguration);
    };

    const handleFilterChange = async (filter: TFilterArrayItem, index: number, isCategory: boolean) => {
        const updatedFieldDataConfiguration: TFieldDataConfiguration = {...fieldDataConfiguration};
        if (updatedFieldDataConfiguration.multiFilter?.filters) {
            const filters = updatedFieldDataConfiguration.multiFilter.filters;
            if (isCategory && fieldDataConfiguration.multiFilter?.type === 'multiple' && filters.length > 0) {
                const filterCombination = `${filter.subModelId}${filter.categoryId}`
                for (let item of filters) {
                    if (`${item.subModelId}${item.categoryId}` === filterCombination) {
                        toast.error('This filter combination already exists');
                        return;
                    }
                }
            }
            if (isCategory && fieldDataConfiguration.multiFilter?.type === 'single' && filters.length > 0) {
                for (let i = 0; i < updatedFieldDataConfiguration.multiFilter.filters.length; i++) {
                    if (updatedFieldDataConfiguration.multiFilter.filters[i].categoryId !== filter.categoryId) {
                        updatedFieldDataConfiguration.multiFilter.filters[i].categoryId = filter.categoryId;
                        updatedFieldDataConfiguration.multiFilter.filters[i].attributeId = '';
                    }
                }
            }

            updatedFieldDataConfiguration.multiFilter.filters[index] = filter;
            setFieldDataConfiguration(updatedFieldDataConfiguration);
            if (isCategory) {
                await fetchAssociatedAttributes(filter.categoryId);
            }
        }
    };

    const handleFilterRangeChange = (value: boolean) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, filterRange: value}));
    }

    const fetchAssociatedAttributes = async (categoryId: string) => {
        try {
            const responseData = await getAssociatedAttributes(categoryId);
            if (responseData?.done) {
                attributesMap[categoryId] = responseData?.data.sort((a: any, b: any) => {
                    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
                });
                setAttributesMap(attributesMap);
            }
        } catch (error) {
            console.log(error, 'Error');
        }
    }

    const fetchCategoryTypesAndCategories = async () => {
        try {
            setCategoryData([]);
            const response = await getCategoryTypeAndCategory(industryId ?? '');
            if (response?.done) {
                setCategoryData(response?.data);
            }
        } catch (e) {
            console.log('Error', e);
        }
    }

    useEffect(() => {
        fetchCategoryTypesAndCategories();
    }, []);

    useEffect(() => {
        if (fieldDataConfiguration?.multiFilter?.filters && fieldDataConfiguration?.multiFilter?.filters.length > 0) {
            fieldDataConfiguration?.multiFilter?.filters?.map((filter) => fetchAssociatedAttributes(filter.categoryId));
        }
    }, [fieldDataConfiguration?.multiFilter?.filters]);


    return (
        <div id={id} className='tw-mt-2 tw-mb-2'>
            <div id={`${id}-header`} className='tw-w-full tw-mb-auto tw-flex tw-items-center tw-justify-between'>
                <label id={`${id}-title`} className='cart-title'>{'Filter'}</label>
                {hasRange ? <div id={`${id}-range-container`} className='tw-flex tw-items-center tw-gap-2 tw-me-16'>
                    <label id={`${id}-range-label`} className='tw-text-sm tw-text-[#747593]'>{FILTERING_RANGE}</label>
                    <ToggleButton 
                        id={`${id}-range-toggle`}
                        name="filtering range" 
                        value={fieldDataConfiguration?.filterRange ?? false}
                        onChange={(e) => {
                            handleFilterRangeChange(e.target.checked)
                        }}/>
                </div> : null}
            </div>
    
            <div id={`${id}-filter-type`} className="tw-flex tw-my-4">
                <div id={`${id}-single-filter-container`} className="tw-flex tw-items-center tw-me-4">
                    <input 
                        id={`${id}-single-radio`}
                        checked={fieldDataConfiguration.multiFilter?.type === 'single'} 
                        type="radio" 
                        value="" 
                        name="inline-radio-group"
                        onChange={() => {
                            if ((fieldDataConfiguration.multiFilter?.filters ?? []).length > 0) {
                                setShowDialog('single');
                                return;
                            }
                            updateMultiFilterType('single');
                        }}
                        className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-focus:ring-blue-500 tw-dark:focus:ring-blue-600 tw-dark:ring-offset-gray-800 tw-focus:ring-2 tw-dark:bg-gray-700 tw-dark:border-gray-600"/>
                    <label 
                        id={`${id}-single-radio-label`}
                        htmlFor="inline-2-radio"
                        className="tw-ms-2 tw-text-sm tw-font-medium tw-text-gray-900 tw-dark:text-gray-300 tw-font-['Noto Sans']">Single
                        Category</label>
                </div>
                <div id={`${id}-multiple-filter-container`} className="tw-flex tw-items-center">
                    <input 
                        id={`${id}-multiple-radio`}
                        checked={fieldDataConfiguration.multiFilter?.type === 'multiple'} 
                        type="radio" 
                        value="" 
                        name="inline-radio-group"
                        onChange={() => {
                            if ((fieldDataConfiguration.multiFilter?.filters ?? []).length > 0) {
                                setShowDialog('multiple');
                                return;
                            }
                            updateMultiFilterType('multiple');
                        }}
                        className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-focus:ring-blue-500 tw-dark:focus:ring-blue-600 tw-dark:ring-offset-gray-800 tw-focus:ring-2 tw-dark:bg-gray-700 tw-dark:border-gray-600"/>
                    <label 
                        id={`${id}-multiple-radio-label`}
                        htmlFor="inline-radio"
                        className="tw-ms-2 tw-text-sm tw-font-medium tw-text-gray-900 tw-dark:text-gray-300 tw-font-['Noto Sans']">Multiple
                        Category</label>
                </div>
            </div>
    
            {
                fieldDataConfiguration?.multiFilter?.filters?.map((filter, filterIndex) => {
                    let categoryTypesToDisplay: any = [];
                    const dataModelFound = networkUserProjectDataModels.project.dataModels.filter((e: any) => e.id === filter.subModelId)
                    if (dataModelFound.length > 0) {
                        const dataModelToUpdate = dataModelFound[0];
                        let subModelCategories = [...dataModelToUpdate.data.productCategories].map((e) => e.data.categories.data.map((category: any) => category.id)).flat(1);
                        if (subModelCategories.length > 0) {
                            let allCategories = structuredClone(categoryData);
                            categoryTypesToDisplay = allCategories.map((item: any) => {
                                item.category = item.category.filter((e: any) => subModelCategories.includes(e.id))
                                return item;
                            }).filter((e: any) => e.category.length > 0);
                        }
                    }
                    const attributes = attributesMap[filter.categoryId] ?? [];
                    return (
                        <div 
                            key={filterIndex} 
                            id={`${id}-filter-item-${filterIndex}`} 
                            className='tw-flex tw-items-center tw-mt-2'
                        >
                            <div id={`${id}-filter-item-${filterIndex}-operator-container`} className='tw-w-[18%] tw-pe-2'>
                                <label id={`${id}-filter-item-${filterIndex}-operator-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{'Operator'}</label>
                                <select 
                                    id={`${id}-filter-item-${filterIndex}-operator-select`}
                                    className="drop-down-dm"
                                    value={filter?.operator ?? ''}
                                    onChange={(event: ChangeEvent<HTMLSelectElement>) => handleFilterChange({
                                        ...filter,
                                        operator: event.target.value === 'Select' ? '' : event.target.value
                                    }, filterIndex, false)}
                                >
                                    <option>
                                        Select
                                    </option>
                                    {(isRangeField ? MULTI_FILTER_VALUES : FILTER_VALUES).map((item, index) => {
                                        return (<option value={item.value} key={item.value + index}>{item.display}</option>
                                        );
                                    })}
                                </select>
                            </div>
                            <div id={`${id}-filter-item-${filterIndex}-submodel-container`} className='tw-w-[37%] tw-ms-4'>
                                <label id={`${id}-filter-item-${filterIndex}-submodel-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{'SubModel'}</label>
                                <select 
                                    id={`${id}-filter-item-${filterIndex}-submodel-select`}
                                    className={`drop-down-dm`}
                                    value={filter?.subModelId ?? ''}
                                    disabled={fieldDataConfiguration.multiFilter?.type === 'single'}
                                    onChange={(event: ChangeEvent<HTMLSelectElement>) => handleFilterChange({
                                        ...filter,
                                        subModelId: event.target.value === 'Select Submodel' ? '' : event.target.value,
                                        categoryId: '',
                                        attributeId: '',
                                    }, filterIndex, false)}
                                >
                                    <option value={''} disabled={true}>
                                        Select Submodel
                                    </option>
                                    {networkUserProjectDataModels?.project.dataModels.map((item: any, index: any) => {
                                        return (
                                            <option value={item.id ?? ''} key={(item.id ?? '') + index}
                                                    disabled={fieldDataConfiguration.multiFilter?.type === 'single'}>
                                                {item.data.name}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            <div id={`${id}-filter-item-${filterIndex}-category-container`} className='tw-w-[37%] tw-ms-4'>
                                <label id={`${id}-filter-item-${filterIndex}-category-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{CATEGORY}</label>
                                <select 
                                    id={`${id}-filter-item-${filterIndex}-category-select`}
                                    className={`drop-down-dm`}
                                    value={filter?.categoryId ?? ''}
                                    onChange={(event: ChangeEvent<HTMLSelectElement>) => handleFilterChange({
                                        ...filter,
                                        categoryId: event.target.value === 'Select Category' ? '' : event.target.value,
                                        attributeId: ''
                                    }, filterIndex, true)}
                                >
                                    <option value={''} disabled={true}>
                                        Select Category
                                    </option>
                                    {categoryTypesToDisplay.map((item: any, index: any) => {
                                        return (
                                            <optgroup className="tw-text-xs" label={item.name} key={item.name}>
                                                {item.category.map((category: any) => {
                                                    return (
                                                        <option value={category.id} key={category.id}>
                                                            {category.name}
                                                        </option>
                                                    );
                                                })}
                                            </optgroup>
                                        );
                                    })}
                                </select>
                            </div>
                            <div id={`${id}-filter-item-${filterIndex}-attribute-container`} className='tw-w-[37%] tw-ms-4'>
                                <label id={`${id}-filter-item-${filterIndex}-attribute-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{'Attribute'}</label>
                                <select 
                                    id={`${id}-filter-item-${filterIndex}-attribute-select`}
                                    className="drop-down-dm"
                                    value={filter?.attributeId ?? ''}
                                    onChange={(event: ChangeEvent<HTMLSelectElement>) => handleFilterChange({
                                        ...filter,
                                        attributeId: event.target.value === 'Select Attribute' ? '' : event.target.value
                                    }, filterIndex, false)}
                                >
                                    <option disabled={false}>
                                        Select Attribute
                                    </option>
                                    {attributes?.map((item: any, index: any) => {
                                        return (
                                            <option value={item.id} key={item.id + index}>
                                                {item.name} {item.type === 'Transformation' ? '(Transformation)' : ''}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            <div id={`${id}-filter-item-${filterIndex}-delete-container`} className='tw-w-[10%] tw-ms-4 tw-mt-3'>
                                <button 
                                    id={`${id}-filter-item-${filterIndex}-delete-btn`}
                                    onClick={() => handleCategoryDelete(filterIndex)}
                                    className='tw-mt-2 tw-w-auto tw-cursor-pointer tw-flex tw-items-center tw-gap-1'
                                >
                                    <DeleteIcon/>
                                </button>
                            </div>
                        </div>
                    );
                })
            }
            <button 
                id={`${id}-add-filter-btn`}
                onClick={createMultiFilter}
                className='tw-mt-4 tw-w-auto tw-cursor-pointer tw-flex tw-items-center tw-gap-1'
            >
                <label id={`${id}-add-filter-label`} className='tw-text-sm tw-text-primary tw-block'>{'Add new filter'}</label>
            </button>
            {(fieldDataConfiguration.multiFilter?.filters?.length ?? 0) > 0 && fieldDataConfiguration.multiFilter?.type === 'single' ?
                <div id={`${id}-logic-selector`} className="tw-flex tw-mt-4 tw-mb-8">
                    <div id={`${id}-all-true-container`} className="tw-flex tw-items-center tw-me-4">
                        <input 
                            id={`${id}-all-true-radio`}
                            checked={fieldDataConfiguration.multiFilter?.logic === 'allTrue'} 
                            type="radio" 
                            value="" 
                            name="inline-radio-group-logic"
                            onChange={() => updateMultiFilterLogic('allTrue')}
                            className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-focus:ring-blue-500 tw-dark:focus:ring-blue-600 tw-dark:ring-offset-gray-800 tw-focus:ring-2 tw-dark:bg-gray-700 tw-dark:border-gray-600"/>
                        <label 
                            id={`${id}-all-true-label`}
                            htmlFor="inline-radio-group-logic"
                            className="tw-ms-2 tw-text-sm tw-font-medium tw-text-gray-900 tw-dark:text-gray-300 tw-font-['Noto Sans']">All
                            True</label>
                    </div>
                    <div id={`${id}-any-true-container`} className="tw-flex tw-items-center">
                        <input 
                            id={`${id}-any-true-radio`}
                            checked={fieldDataConfiguration.multiFilter?.logic === 'anyTrue'} 
                            type="radio" 
                            value="" 
                            name="inline-radio-group-logic"
                            onChange={() => updateMultiFilterLogic('anyTrue')}
                            className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-focus:ring-blue-500 tw-dark:focus:ring-blue-600 tw-dark:ring-offset-gray-800 tw-focus:ring-2 tw-dark:bg-gray-700 tw-dark:border-gray-600"/>
                        <label 
                            id={`${id}-any-true-label`}
                            htmlFor="inline-radio-group-logic"
                            className="tw-ms-2 tw-text-sm tw-font-medium tw-text-gray-900 tw-dark:text-gray-300 tw-font-['Noto Sans']">Any
                            True</label>
                    </div>
                </div> : null}
            {showDialog != null ? <ConfirmDialog 
                id={`${id}-confirm-dialog`}
                title={'Alert'}
                message={'The filters you ve added will be cleared, are you sure you want to continue ?'}
                onClose={() => {
                    setShowDialog(null);
                }} 
                onConfirm={() => {
                    updateMultiFilterType(showDialog)
                    setShowDialog(null);
                }}
            /> : null}
        </div>
    );
}

export default ConfigurationFilterComponent;